import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { Box, Popover, Typography } from '@mui/material';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { SendContext } from '../../SendContext';
import { translateWithFallback } from '../../util';
import {
  CharacterIcon,
  mapCharacterIconToFilePath,
} from '../SceneCanvas/CharacterIcon';
import { Character } from '../SceneCanvas/types';
import { EvoachButton } from '../Button';
import { theme as evoachDefaultTheme } from '../../theme/theme';

import { CharacterIconPicker } from './CharacterIconPicker';

export interface SceneCanvasCharacterSelectorComponent extends BaseComponent {
  type: 'sceneCanvasCharacterSelector';
  props: SceneCanvasCharacterSelectorProps;
}

export interface SceneCanvasCharacterSelectorItem extends Character {
  type: string;
  value: string;
}

export interface SceneCanvasCharacterSelectorProps {
  elements: SceneCanvasCharacterSelectorItem[];
  buttonText: string;
  saveResultTo: string;
  isEcho?: boolean;
}

export const SceneCanvasCharacterSelector: React.FC<SceneCanvasCharacterSelectorProps> = ({
  elements,
  buttonText,
  saveResultTo,
  isEcho = false,
}) => {
  const { send } = useContext(SendContext);
  const [items, setItems] = useState<SceneCanvasCharacterSelectorItem[]>(
    elements
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentIndex, setcurrentIndex] = useState<number>(0);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const changeCharacterIcon = (icon: CharacterIcon) => {
    setAnchorEl(null);
    items[currentIndex].icon = icon;
    setItems(items);
  };
  return (
    <div>
      {items &&
        items.map((item: SceneCanvasCharacterSelectorItem, index: number) => {
          return (
            <Box
              sx={[
                {
                  '&:hover': {
                    backgroundColor: evoachDefaultTheme.palette.secondary.main,
                  },
                },
                {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  userSelect: 'none',
                  margin: '0 0 8px 0',
                  borderRadius: '6px',
                  backgroundColor: evoachDefaultTheme.palette.secondary.light,
                },
              ]}
              key={`masterboxsccachsel${index}`}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Typography
                  style={{
                    paddingLeft: '8px',
                    paddingRight: '8px',
                    paddingTop: '17px',
                    paddingBottom: '17px',
                  }}
                >
                  {item.value}
                </Typography>
              </div>

              <Box
                sx={{
                  maxWidth: '250px',
                  minWidth: '50px',
                  visibility: 'visible',
                  width: '85px',
                  height: '60px',
                  justifyContent: 'center',
                  display: 'flex',
                }}
                onClick={(event: any) => {
                  if (!isEcho) {
                    setcurrentIndex(index);
                    setAnchorEl(event.currentTarget);
                  }
                }}
                key={`masterboxsccachselimage${index}`}
              >
                <img
                  alt={item.icon as string}
                  style={{ height: '40px', padding: '10px 0' }}
                  src={mapCharacterIconToFilePath(item.icon)}
                />
              </Box>
            </Box>
          );
        })}
      {!isEcho && (
        <>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
          >
            <CharacterIconPicker
              onChange={(icon: CharacterIcon) => changeCharacterIcon(icon)}
            />
          </Popover>

          <EvoachButton
            disabled={
              !!items.find((item) => item.icon === CharacterIcon.DEFAULT)
            }
            onClick={() => {
              send({
                type: 'writeToContext',
                payload: {
                  [saveResultTo]: items,
                },
              });
              send({ type: 'next' });
            }}
          >
            {buttonText}
          </EvoachButton>
        </>
      )}
    </div>
  );
};

export const renderSceneCanvasCharacterSelectorAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;

    const dataSourceList = payload.getValuesFrom;

    // if getValuesFrom is set, then use it to retrieve elements
    // if not, retrieve elements directly from payload
    const elements =
      dataSourceList && Array.isArray(dataSourceList)
        ? dataSourceList
            .filter(
              (datasource: string) =>
                datasource !== undefined &&
                datasource !== null &&
                datasource.trim() !== '' &&
                context.userData[datasource] !== undefined
            )
            .map((dataSource: string) => {
              const originalData = context.userData[dataSource];

              return originalData.map((entry: any) => {
                if (typeof entry === 'string') {
                  return {
                    type: 'character',
                    value: entry,
                    icon: CharacterIcon.DEFAULT,
                    name: entry,
                  };
                } else {
                  return {
                    type: entry.type ?? 'character',
                    value: entry.value ?? entry.name,
                    icon: entry.icon ?? CharacterIcon.DEFAULT,
                    name: entry.name ?? entry.value,
                  };
                }
              });
            })
            .flat()
        : payload.elements;

    // console.log(elements);

    newContext.widgetData.push({
      type: 'sceneCanvasCharacterSelector',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        isEcho: payload.isEcho,
      },
    });
    return newContext;
  });
};
